// Implements: F-UIX-010
/**
 * The one error shape every consumer of the API sees.
 *
 * `httpClient` translates the envelope `{"error": {"code", "message"}}` into
 * this class and throws it; nothing else in the application constructs one
 * from a response. Two decisions are worth stating:
 *
 *   1. A body that does NOT match the envelope becomes `code: "UNKNOWN"` with
 *      the real status, never an `undefined` that travels one more layer
 *      before failing somewhere with no context left (design section 4).
 *   2. The response headers travel with the error. The second-factor branch of
 *      the login screen reads `X-Second-Factor` from here the day the backend
 *      publishes it, and it is readable without `Access-Control-Expose-Headers`
 *      precisely because the origin is the same one (design DD7).
 */

import type { ErrorEnvelope } from "../../types/api";
import type { ApiFailure, ErrorDetails } from "../../types/errors";

/** The code used when the response carries no readable envelope. */
export const UNKNOWN_ERROR_CODE = "UNKNOWN";

/** The header phase-3 will publish so the second factor stops depending on bytes. */
export const SECOND_FACTOR_HEADER = "X-Second-Factor";

/** What that header is allowed to say. */
export type SecondFactorHint = "required" | "invalid";

export class ApiError extends Error implements ApiFailure {
  readonly status: number;
  readonly code: string;
  readonly headers: Headers;
  /** The envelope's optional `reason` (phase 7: which usage limit cut), or `null`. */
  readonly reason: string | null;
  /** The envelope's optional `renews_at` (ISO 8601, when the limit renews), or `null`. */
  readonly renewsAt: string | null;
  /** The envelope's optional `details` when it names a `field` and a `reason`, or `null` (F-GEN-036). */
  readonly details: ErrorDetails | null;

  constructor(
    status: number,
    code: string,
    message: string,
    headers: Headers,
    publicFields: PublicFields = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.headers = headers;
    this.reason = publicFields.reason ?? null;
    this.renewsAt = publicFields.renewsAt ?? null;
    this.details = publicFields.details ?? null;
  }
}

/**
 * The optional public fields an envelope may carry beside `code` and `message`.
 *
 * The backend renders exactly `reason` and `renews_at`, as strings, and nothing
 * else (phase 7, D-P7-5). A value of any other type is dropped here rather than
 * trusted: the mapping to a sentence reads strings only.
 */
export interface PublicFields {
  readonly reason?: string | null | undefined;
  readonly renewsAt?: string | null | undefined;
  readonly details?: ErrorDetails | null | undefined;
}

function detailsOf(value: unknown): ErrorDetails | null {
  if (typeof value !== "object" || value === null) return null;
  const { field, reason } = value as { field?: unknown; reason?: unknown };
  return typeof field === "string" && typeof reason === "string" ? { field, reason } : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function publicFieldsOf(envelope: ErrorEnvelope): PublicFields {
  return {
    reason: stringOrNull(envelope.error.reason),
    renewsAt: stringOrNull(envelope.error.renews_at),
    details: detailsOf(envelope.error.details),
  };
}

/** Whether an unknown value has the exact shape of the error envelope. */
export function isErrorEnvelope(body: unknown): body is ErrorEnvelope {
  if (typeof body !== "object" || body === null) return false;
  const envelope = (body as { error?: unknown }).error;
  if (typeof envelope !== "object" || envelope === null) return false;
  const { code, message } = envelope as { code?: unknown; message?: unknown };
  return typeof code === "string" && typeof message === "string";
}

/**
 * Build the error for a failed response.
 *
 * The raw body is kept as the message only when it is a non-empty string, so a
 * proxy that answers HTML still produces something a log can show while the
 * code stays `UNKNOWN` and no branch can mistake it for a contract answer.
 */
export function toApiError(status: number, body: unknown, headers: Headers): ApiError {
  if (isErrorEnvelope(body)) {
    return new ApiError(status, body.error.code, body.error.message, headers, publicFieldsOf(body));
  }
  const message = typeof body === "string" && body.trim() !== "" ? body : `HTTP ${String(status)}`;
  return new ApiError(status, UNKNOWN_ERROR_CODE, message, headers);
}

/**
 * The second-factor hint the backend published, or `undefined`.
 *
 * `undefined` is not "no second factor": it means the header was absent, which
 * is what sends the caller to the byte comparison of DD7 step 2.
 */
export function secondFactorHint(error: ApiError): SecondFactorHint | undefined {
  const value = error.headers.get(SECOND_FACTOR_HEADER);
  return value === "required" || value === "invalid" ? value : undefined;
}
